import { Component, OnInit } from '@angular/core';

import { AlertService } from '@shared/services/alert.service';
import { HarpValidators } from '@shared/utils/validators';
import { InputConfig } from '@shared/models/input-config.model';
import { INPUT_CONFIGS, TEXT_CONSTANTS } from '@shared/constants';
import { RegexService, RegEx } from '@shared/services/regex.service';
import { HarpLoginService } from '@login/services/harp-login.service';

import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {
  CONFIRM_NEW_PASSWORD_VALIDATORS,
  NEW_PASSWORD_VALIDATORS,
} from './change-password.constants';

const UNEXPECTED_ERROR_MSG =
  'An unexpected error has occurred. Please try again later.';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnInit {
  isPasswordChanged = false;

  changePasswordForm: UntypedFormGroup;
  oldPasswordInputConfig: InputConfig;
  newPasswordInputConfig: InputConfig;
  confirmNewPasswordInputConfig: InputConfig;

  constructor(
    private harpLoginService: HarpLoginService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private regexService: RegexService,
  ) {}

  ngOnInit() {
    this.initForm();
  }

  private initForm() {
    const passwordRegex = this.regexService.get(RegEx.PASSWORD_REGEX);

    this.changePasswordForm = this.fb.group({
      oldPassword: [
        '',
        [
          HarpValidators.required(
            TEXT_CONSTANTS.ERROR_MESSAGE_REQUIRED('Password'),
          ),
        ],
      ],
      newPassword: ['', NEW_PASSWORD_VALIDATORS(passwordRegex)],
      confirmNewPassword: ['', CONFIRM_NEW_PASSWORD_VALIDATORS()],
    });

    this.newPasswordControl.valueChanges.subscribe({
      next: password => {
        this.confirmNewPasswordControl.setValidators(
          CONFIRM_NEW_PASSWORD_VALIDATORS(password),
        );

        this.confirmNewPasswordControl.updateValueAndValidity();
      },
    });

    this.oldPasswordInputConfig = INPUT_CONFIGS.PASSWORD({
      form: this.changePasswordForm,
      name: 'oldPassword',
      label: 'Old Password',
    });

    this.newPasswordInputConfig = INPUT_CONFIGS.PASSWORD({
      form: this.changePasswordForm,
      name: 'newPassword',
      label: 'New Password',
      trimLeadingAndTrailingWhitespace: true,
    });

    this.confirmNewPasswordInputConfig = INPUT_CONFIGS.PASSWORD({
      form: this.changePasswordForm,
      name: 'confirmNewPassword',
      label: 'Confirm New Password',
      trimLeadingAndTrailingWhitespace: true,
    });
  }

  passwordExpCheck() {
    const { oldPassword, newPassword } = this.changePasswordForm.value;

    const pwdInfo = {
      id: this.userId,
      oldPassword,
      newPassword,
    };

    this.harpLoginService.passwordExpiredCheck(pwdInfo).subscribe(
      (httpResponse: any) => {
        if (!httpResponse) {
          this.alertService.setAlert(UNEXPECTED_ERROR_MSG);
          return;
        }

        if (!httpResponse.errorCode) {
          this.isPasswordChanged = true;
          this.alertService.clearAlert();
          return;
        }

        let errorMessage = '';
        if (httpResponse.errorCauses?.length > 0) {
          httpResponse.errorCauses.forEach(cause => {
            errorMessage += cause.errorSummary;
          });
        } else {
          errorMessage = httpResponse.errorSummary;
        }
        this.alertService.setAlert(errorMessage);
      },
      () => this.alertService.setAlert(UNEXPECTED_ERROR_MSG),
    );
  }

  private get newPasswordControl() {
    return this.changePasswordForm.get('newPassword');
  }

  private get confirmNewPasswordControl() {
    return this.changePasswordForm.get('confirmNewPassword');
  }

  private get userId() {
    const { userId } = this.harpLoginService.getUserProfile();
    return userId;
  }
}
